// Tracking utilities for Google Ads, GA4, Meta Pixel
// DSGVO-compliant: Google Consent Mode v2 default = denied. Updated on user consent.
import { trackLead } from '@/lib/tracking';

const DEBUG_TRACKING = true;

const CONSENT_KEY = 'bekaj-cookie-consent';

const hasMarketingConsent = () => {
  try {
    const c = localStorage.getItem(CONSENT_KEY);
    if (!c) return false;
    return JSON.parse(c).marketing === true;
  } catch {
    return false;
  }
};

export const getConsent = () => {
  try {
    const c = localStorage.getItem(CONSENT_KEY);
    if (!c) return null;
    return JSON.parse(c);
  } catch {
    return null;
  }
};

// ---- Google Consent Mode v2 ----
// Must be set BEFORE any tracking script loads
const ensureGtagStub = () => {
  if (!window.dataLayer) window.dataLayer = [];
  if (!window.gtag) {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
};

export const initConsentDefaults = () => {
  ensureGtagStub();
  // Idempotent: defaults are already set inline in public/index.html before any tag loads.
  // Skip re-pushing to avoid duplicate consent-default entries in dataLayer.
  if (window.__bekajConsentDefaultsSet) {
    if (DEBUG_TRACKING) console.log('[Consent v2] defaults already set (via index.html), skipping');
    return;
  }
  // Fallback (z. B. lokale Dev-Umgebung ohne index.html-Snippet)
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  window.__bekajConsentDefaultsSet = true;
  if (DEBUG_TRACKING) console.log('[Consent v2] defaults set to denied');
};

export const updateConsent = (granted) => {
  ensureGtagStub();
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: state,
    analytics_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
  if (DEBUG_TRACKING) console.log(`[Consent v2] updated -> ${state}`);
};

// ---- Google Ads Conversion (AW-16933113405) ----
// Delegiert an das zentrale Label-System in src/lib/tracking.js:
// lead→formular (echtes Label), whatsapp/call→eigene Labels (bis zur
// Eintragung 'XXXX' = nur dataLayer-Event, KEINE Ads-Conversion).
export const fireGoogleAdsConversion = (bucket = 'lead', gewerk = null) => {
  const map = { lead: 'formular', whatsapp: 'whatsapp', call: 'anruf' };
  return trackLead(map[bucket] || 'formular', gewerk);
};

export const trackEvent = (eventName, eventData = {}) => {
  if (!hasMarketingConsent()) {
    if (DEBUG_TRACKING) console.log('[TRACKING] blocked (no consent):', eventName);
    return;
  }
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...eventData });
  }
  if (window.fbq) {
    const metaEvents = {
      generate_lead: 'Lead',
      contact_whatsapp: 'Contact',
      call_click: 'Contact',
      form_start: 'InitiateCheckout',
    };
    const e = metaEvents[eventName];
    if (e) window.fbq('track', e, eventData);
  }

  // Google Ads Conversions laufen ausschließlich über lib/tracking.js trackLead()
  // an den Klickstellen bzw. auf /danke — hier KEINE Conversion-Delegation mehr
  // (verhinderte doppelte lead_*-Events pro Klick).
};

export const initTracking = (gtmId, metaPixelId, ga4Id) => {
  if (!hasMarketingConsent()) {
    if (DEBUG_TRACKING) console.log('[TRACKING] init blocked (no consent)');
    return;
  }

  ensureGtagStub();
  updateConsent(true);

  // GTM
  if (gtmId && gtmId.trim() && !document.querySelector('script[data-gtm]')) {
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const s = document.createElement('script');
    s.async = true;
    s.dataset.gtm = '1';
    s.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(s);
    if (DEBUG_TRACKING) console.log('[GTM] loading');
  }

  // GA4 (independent of GTM) — Test-/Platzhalter-IDs niemals laden
  if (ga4Id && ga4Id.trim() && !/^G-TEST/i.test(ga4Id.trim()) && !document.querySelector('script[data-ga4]')) {
    const s = document.createElement('script');
    s.async = true;
    s.dataset.ga4 = '1';
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(s);
    window.gtag('js', new Date());
    window.gtag('config', ga4Id, { anonymize_ip: true });
    if (DEBUG_TRACKING) console.log('[GA4] configured');
  }

  // Meta Pixel
  if (metaPixelId && metaPixelId.trim() && !window.fbq) {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', metaPixelId);
    window.fbq('track', 'PageView');
    if (DEBUG_TRACKING) console.log('[Meta] pixel loaded');
  }
};

export const removeTracking = () => {
  ensureGtagStub();
  updateConsent(false);
  if (window.fbq) {
    try {
      delete window.fbq;
      delete window._fbq;
    } catch (e) {
      // Some browsers reject deletion of non-configurable globals; this is safe to ignore.
      if (DEBUG_TRACKING) console.warn('[Tracking] Could not remove fbq global:', e?.message);
    }
  }
  document.querySelectorAll('script[src*="googletagmanager.com"], script[src*="facebook.net"]').forEach((s) => s.remove());
  if (DEBUG_TRACKING) console.log('[TRACKING] removed & consent revoked');
};
